import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {isEmpty, isNil, isNotEmpty, isNotNil} from 'ramda';

import {PurchasePriceMarginTypeSchema} from '@dms/api/sales';
import {i18n} from '@dms/i18n';

import {Nullish} from 'shared/web';

import {PURCHASE_PRICE_RULE_NAME_MAX_LENGTH} from '../../../constants/PURCHASE_PRICE_RULE_NAME_MAX_LENGTH';
import {hasAtMostDecimalPlaces} from '../../../utils/hasAtMostDecimalPlaces';

const MAX_PERCENTAGE = 100;
// BigDecimal amounts are stored in tenant currency with at most cents.
const MAX_DECIMAL_PLACES = 2;

const PriceBandFormSchema = z.object({
  /** The last band is open-ended and its bound is not entered. */
  upperBound: z.number().nullable(),
  marginType: PurchasePriceMarginTypeSchema,
  marginValue: z.number().nullable(),
});

const ModifierFormSchema = z.object({
  enabled: z.boolean(),
  percentage: z.number().nullable(),
});

const ThresholdModifierFormSchema = ModifierFormSchema.extend({
  threshold: z.number().int().nullable(),
});

const BaseFormSchema = z.object({
  name: z.string(),
  // A multi choice reports `null` once it is cleared.
  scope: z.object({
    makes: z.array(z.string()).nullable(),
    modelFamilies: z.array(z.string()).nullable(),
    trims: z.array(z.string()).nullable(),
  }),
  priceBands: z.array(PriceBandFormSchema),
  warrantyCost: z.number().nullable(),
  modifiers: z.object({
    electricVehicle: ModifierFormSchema,
    highMileage: ThresholdModifierFormSchema,
    higherAge: ThresholdModifierFormSchema,
  }),
});

export type PurchasePriceRuleFormValues = z.infer<typeof BaseFormSchema>;
export type PurchasePriceRulePriceBandFormValues = z.infer<typeof PriceBandFormSchema>;

type ModifierFormValues = z.infer<typeof ModifierFormSchema>;
type ThresholdModifierFormValues = z.infer<typeof ThresholdModifierFormSchema>;
type IssuePath = (string | number)[];
type AddIssue = (path: IssuePath, message: string) => void;

const validateModifier = (modifier: ModifierFormValues, path: IssuePath, addIssue: AddIssue) => {
  if (modifier.enabled && isNil(modifier.percentage)) {
    addIssue([...path, 'percentage'], i18n.t('general.errors.mixed.required'));
  }
};

const validateThresholdModifier = (
  modifier: ThresholdModifierFormValues,
  path: IssuePath,
  missingThresholdMessage: string,
  addIssue: AddIssue
) => {
  validateModifier(modifier, path, addIssue);

  if (!modifier.enabled) {
    return;
  }

  if (isNil(modifier.threshold)) {
    addIssue([...path, 'threshold'], missingThresholdMessage);
  } else if (modifier.threshold <= 0) {
    addIssue([...path, 'threshold'], i18n.t('general.errors.number.positive'));
  }
};

const getUpperBoundMessage = (upperBound: number | null, previousBound: number | Nullish) =>
  match(upperBound)
    .with(Pattern.nullish, () => i18n.t('general.errors.mixed.required'))
    .when(
      (bound) => bound <= 0,
      () => i18n.t('general.errors.number.positive')
    )
    .when(
      (bound) => isNotNil(previousBound) && bound <= previousBound,
      () => i18n.t('page.settings.purchasePrice.ruleEditor.validation.boundMustAscend')
    )
    .otherwise(() => undefined);

const getMarginValueMessage = (band: PurchasePriceRuleFormValues['priceBands'][number]) =>
  match(band.marginValue)
    .with(Pattern.nullish, () => i18n.t('general.errors.mixed.required'))
    .when(
      (value) => value < 0,
      () => i18n.t('general.errors.number.greaterOrEqual', {min: 0})
    )
    .when(
      (value) => band.marginType === 'PERCENTAGE' && value > MAX_PERCENTAGE,
      () => i18n.t('general.errors.number.lowerOrEqual', {max: MAX_PERCENTAGE})
    )
    .otherwise(() => undefined);

const getWarrantyCostMessage = (warrantyCost: number | null) =>
  match(warrantyCost)
    .with(Pattern.nullish, () => i18n.t('general.errors.mixed.required'))
    .when(
      (cost) => cost < 0,
      () => i18n.t('general.errors.number.greaterOrEqual', {min: 0})
    )
    .when(
      (cost) => !hasAtMostDecimalPlaces(cost, MAX_DECIMAL_PLACES),
      () => i18n.t('general.validations.maximumDecimalPlaces', {maximum: MAX_DECIMAL_PLACES})
    )
    .otherwise(() => undefined);

/**
 * Mirrors the validation of the backend, so that a request the backend would reject with a 400 is
 * stopped in the form with a message next to the field. Every message is resolved when the form is
 * validated, so a language change is respected.
 */
const validateRule = (
  values: PurchasePriceRuleFormValues,
  context: z.RefinementCtx,
  isSystemRule: boolean
) => {
  const addIssue: AddIssue = (path, message) =>
    context.addIssue({code: z.ZodIssueCode.custom, path, message});
  const requiredMessage = i18n.t('general.errors.mixed.required');

  if (!isSystemRule) {
    const name = values.name.trim();

    if (isEmpty(name)) {
      addIssue(['name'], requiredMessage);
    } else if (name.length > PURCHASE_PRICE_RULE_NAME_MAX_LENGTH) {
      addIssue(
        ['name'],
        i18n.t('general.errors.string.max', {max: PURCHASE_PRICE_RULE_NAME_MAX_LENGTH})
      );
    }

    const makes = values.scope.makes ?? [];

    if (isEmpty(makes)) {
      addIssue(['scope', 'makes'], requiredMessage);
    }

    if (makes.length !== 1) {
      const message = i18n.t(
        'page.settings.purchasePrice.ruleEditor.validation.singleMakeRequired'
      );

      if (isNotEmpty(values.scope.modelFamilies ?? [])) {
        addIssue(['scope', 'modelFamilies'], message);
      }

      if (isNotEmpty(values.scope.trims ?? [])) {
        addIssue(['scope', 'trims'], message);
      }
    }
  }

  if (isEmpty(values.priceBands)) {
    addIssue(['priceBands'], requiredMessage);
  }

  const lastBandIndex = values.priceBands.length - 1;

  values.priceBands.forEach((band, index) => {
    if (index !== lastBandIndex) {
      const previousBound = values.priceBands[index - 1]?.upperBound;
      const upperBoundMessage = getUpperBoundMessage(band.upperBound, previousBound);

      if (isNotNil(upperBoundMessage)) {
        addIssue(['priceBands', index, 'upperBound'], upperBoundMessage);
      }
    }

    const marginValueMessage = getMarginValueMessage(band);

    if (isNotNil(marginValueMessage)) {
      addIssue(['priceBands', index, 'marginValue'], marginValueMessage);
    }
  });

  const warrantyCostMessage = getWarrantyCostMessage(values.warrantyCost);

  if (isNotNil(warrantyCostMessage)) {
    addIssue(['warrantyCost'], warrantyCostMessage);
  }

  // The values of a disabled modifier are kept, but never validated.
  validateModifier(values.modifiers.electricVehicle, ['modifiers', 'electricVehicle'], addIssue);
  validateThresholdModifier(
    values.modifiers.highMileage,
    ['modifiers', 'highMileage'],
    i18n.t('page.settings.purchasePrice.ruleEditor.modifiers.highMileage.thresholdRequired'),
    addIssue
  );
  validateThresholdModifier(
    values.modifiers.higherAge,
    ['modifiers', 'higherAge'],
    i18n.t('page.settings.purchasePrice.ruleEditor.modifiers.higherAge.thresholdRequired'),
    addIssue
  );
};

/** Schema of a custom rule. */
export const PurchasePriceRuleFormSchema = BaseFormSchema.superRefine((values, context) =>
  validateRule(values, context, false)
);

/** Schema of the system rule, whose name and scope cannot be changed. */
export const SystemPurchasePriceRuleFormSchema = BaseFormSchema.superRefine((values, context) =>
  validateRule(values, context, true)
);
